import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import BillingNav from './BillingNav';
import { BillingPage } from './billingUi';
import {
  fetchAdjustments,
  createAdjustment,
  formatMoney,
} from '../../services/billingApi';
import { fetchTenants, fetchLookups } from '../../services/licensingApi';

/** Spec — Credits & Adjustments */
const BillingCredits = () => {
  const [rows, setRows] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    tenantId: '',
    invoiceId: '',
    adjustmentType: 'CREDIT',
    amount: '',
    reason: '',
    effectiveDate: new Date().toISOString().slice(0, 10),
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [adj, tens, luk] = await Promise.all([
        fetchAdjustments(),
        fetchTenants(),
        fetchLookups({ grouped: true }),
      ]);
      setRows(adj);
      setTenants(tens);
      setTypes(luk?.ADJUSTMENT_TYPE || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    try {
      await createAdjustment({
        ...form,
        invoiceId: form.invoiceId || null,
      });
      toast.success('Adjustment saved');
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <BillingPage>
      <BillingNav title="Credits & Adjustments" />
      <Button variant="contained" sx={{ mb: 2 }} onClick={() => setOpen(true)}>
        Add credit / adjustment
      </Button>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Effective</TableCell>
              <TableCell>Tenant</TableCell>
              <TableCell>Invoice</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Approved by</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary">No adjustments.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.adjustmentId}>
                  <TableCell>{String(r.effectiveDate || '').slice(0, 10)}</TableCell>
                  <TableCell>{r.tenantName}</TableCell>
                  <TableCell>{r.invoiceNumber || '—'}</TableCell>
                  <TableCell>{r.adjustmentType}</TableCell>
                  <TableCell>{r.reason || '—'}</TableCell>
                  <TableCell align="right">{formatMoney(r.amount)}</TableCell>
                  <TableCell>{r.approvedBy || '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Credit / Adjustment</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Tenant</InputLabel>
                <Select
                  label="Tenant"
                  value={form.tenantId}
                  onChange={(e) => setForm((f) => ({ ...f, tenantId: e.target.value }))}
                >
                  {tenants.map((t) => (
                    <MenuItem
                      key={t.tenantId || t.tenant_id}
                      value={String(t.tenantId || t.tenant_id)}
                    >
                      {t.tenantName || t.tenant_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={form.adjustmentType}
                  onChange={(e) => setForm((f) => ({ ...f, adjustmentType: e.target.value }))}
                >
                  {(types.length
                    ? types
                    : [
                        { code: 'CREDIT', label: 'Credit' },
                        { code: 'DISCOUNT', label: 'Discount' },
                        { code: 'CORRECTION', label: 'Correction' },
                      ]
                  ).map((t) => (
                    <MenuItem key={t.code} value={t.code}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Amount"
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Invoice ID (optional)"
                value={form.invoiceId}
                onChange={(e) => setForm((f) => ({ ...f, invoiceId: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Reason"
                value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Effective date"
                InputLabelProps={{ shrink: true }}
                value={form.effectiveDate}
                onChange={(e) => setForm((f) => ({ ...f, effectiveDate: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </BillingPage>
  );
};

export default BillingCredits;
